import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import LineEditor from "../../components/estimating/LineEditor";
import TotalsPanel from "../../components/estimating/TotalsPanel";
import QuotesPanel from "../../components/estimating/QuotesPanel";
import RevisionsPanel from "../../components/estimating/RevisionsPanel";
import TakeoffsPanel from "../../components/estimating/TakeoffsPanel";
import { BTN, INPUT, errMsg } from "../../components/crm/crmUtil";

const PCTS = [["overhead_pct", "Overhead %"], ["markup_pct", "Profit / markup %"], ["contingency_pct", "Contingency %"], ["tax_pct", "Tax %"]];
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const newLine = () => ({ id: uid(), type: "labor", description: "", qty: 0, unit: "ea", unit_cost: 0, source: { kind: "estimator_assumption", ref_id: null } });
const fromEstimate = (e) => ({
  sections: e.sections || [], allowances: e.allowances || [], assumptions: e.assumptions || [], exclusions: e.exclusions || [],
  overhead_pct: e.overhead_pct ?? 0, markup_pct: e.markup_pct ?? 15, contingency_pct: e.contingency_pct ?? 5, tax_pct: e.tax_pct ?? 0,
});
const toBody = (d) => ({
  ...d, ...Object.fromEntries(PCTS.map(([k]) => [k, Number(d[k] || 0)])),
  allowances: d.allowances.map((a) => ({ label: a.label, amount: Number(a.amount || 0) })),
  assumptions: d.assumptions.filter(Boolean), exclusions: d.exclusions.filter(Boolean),
  sections: d.sections.map((s) => ({ id: s.id, name: s.name, lines: s.lines.map((l) => ({
    id: l.id, type: l.type, description: l.description || "—", qty: Number(l.qty || 0), unit: l.unit || "ea", unit_cost: Number(l.unit_cost || 0),
    source: { kind: l.source.kind, ref_id: l.source.ref_id || null }, ...(l.takeoff_id && { takeoff_id: l.takeoff_id }), ...(l.cost_code && { cost_code: l.cost_code }) })) })),
});

// B5 part 2a: estimate editor. Every number shown comes from the server (calc preview or saved estimate).
export default function EstimateEditor() {
  const { id } = useParams();
  const [est, setEst] = useState(null);
  const [doc, setDoc] = useState(null);
  const [calc, setCalc] = useState(null);
  const [stale, setStale] = useState(false);
  const [rates, setRates] = useState([]);
  const [takeoffs, setTakeoffs] = useState([]);
  const [msg, setMsg] = useState({ ok: "", err: "" });
  const timer = useRef(null);
  const load = useCallback(async () => {
    try {
      const e = (await api.get(`/estimates/${id}`)).data;
      setEst(e); setDoc(fromEstimate(e)); setCalc(e.money ? e : null);
      setTakeoffs((await api.get(`/estimating/estimates/${id}/takeoffs`)).data);
    } catch (x) { setMsg({ ok: "", err: errMsg(x) }); }
  }, [id]);
  useEffect(() => { load(); api.get("/estimating/rates").then((r) => setRates(r.data)).catch(() => {}); }, [load]);
  const edit = (next) => {
    setDoc(next); setStale(true); clearTimeout(timer.current);
    timer.current = setTimeout(() => api.post(`/estimating/estimates/${id}/calc`, toBody(next))
      .then((r) => { setCalc(r.data); setMsg((m) => ({ ...m, err: "" })); }).catch((x) => setMsg({ ok: "", err: errMsg(x) })).finally(() => setStale(false)), 400);
  };
  const save = async () => {
    try { const e = (await api.put(`/estimating/estimates/${id}/editor`, toBody(doc))).data; setEst(e); setDoc(fromEstimate(e)); setCalc(e); setMsg({ ok: "Saved. Totals recalculated on the server.", err: "" }); }
    catch (x) { setMsg({ ok: "", err: errMsg(x) }); }
  };
  const setSection = (i, s) => edit({ ...doc, sections: doc.sections.map((x, j) => (j === i ? s : x)) });
  const lines = (doc?.sections || []).flatMap((s) => s.lines);
  const locked = ["approved", "sent", "accepted"].includes(est?.status);
  const listField = (k, label) => (
    <label htmlFor={`est-${k}`} className="text-sm block">{label} (one per line)
      <textarea id={`est-${k}`} rows={3} value={doc[k].join("\n")} onChange={(e) => edit({ ...doc, [k]: e.target.value.split("\n") })} className={`${INPUT} py-2`} data-testid={`est-${k}`} />
    </label>
  );
  return (
    <AppShell title={est?.name || "Estimate"} subtitle={est ? `v${est.version} · ${est.status}${locked ? " · locked" : ""}` : ""}>
      <div className="mx-auto w-full max-w-[1200px] px-3 md:px-6 space-y-4" data-testid="estimate-editor">
        <Link to="/crm/estimates" className="text-sm underline inline-flex min-h-[44px] items-center" data-testid="est-back">← Estimates</Link>
        {msg.err && <p role="alert" className="text-sm text-red-700" data-testid="est-error">{msg.err}</p>}
        {msg.ok && <p role="status" className="text-sm text-green-800" data-testid="est-saved">{msg.ok}</p>}
        {!doc ? <p className="font-mono text-xs" data-testid="est-loading">Loading…</p> : (
          <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
            <div className="space-y-4 min-w-0">
              {doc.sections.map((s, i) => (
                <section key={s.id} className="border border-erf-borderCream rounded bg-white p-3 space-y-2" data-testid={`section-${s.id}`}>
                  <label htmlFor={`sec-${s.id}`} className="text-sm block">Section<input id={`sec-${s.id}`} value={s.name} onChange={(e) => setSection(i, { ...s, name: e.target.value })} className={INPUT} data-testid={`sec-name-${s.id}`} /></label>
                  <ul className="space-y-2">{s.lines.map((l, j) => <LineEditor key={l.id} line={l} rates={rates} takeoffs={takeoffs}
                    onChange={(nl) => setSection(i, { ...s, lines: s.lines.map((x, k) => (k === j ? nl : x)) })}
                    onRemove={() => setSection(i, { ...s, lines: s.lines.filter((_, k) => k !== j) })} />)}</ul>
                  <button type="button" onClick={() => setSection(i, { ...s, lines: [...s.lines, newLine()] })} className={`${BTN} border border-erf-borderCream`} data-testid={`sec-add-line-${s.id}`}>Add line</button>
                </section>
              ))}
              <button type="button" disabled={locked} onClick={() => edit({ ...doc, sections: [...doc.sections, { id: uid(), name: "New section", lines: [newLine()] }] })} className={`${BTN} border border-erf-borderCream`} data-testid="est-add-section">Add section</button>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {PCTS.map(([k, label]) => <label key={k} htmlFor={`est-${k}`} className="text-sm">{label}<input id={`est-${k}`} type="number" min={0} max={100} step="0.01" value={doc[k]} onChange={(e) => edit({ ...doc, [k]: e.target.value })} className={INPUT} data-testid={`est-${k}`} /></label>)}
              </div>
              <div className="grid gap-2 md:grid-cols-2">{listField("assumptions", "Assumptions")}{listField("exclusions", "Exclusions")}</div>
              <TakeoffsPanel eid={id} takeoffs={takeoffs} onAdded={load} />
              <QuotesPanel eid={id} lines={lines} onSelected={load} />
              <RevisionsPanel eid={id} />
            </div>
            <div className="space-y-3 lg:sticky lg:top-4 self-start">
              <TotalsPanel calc={calc} stale={stale} />
              <button type="button" disabled={locked} onClick={save} className={`${BTN} w-full bg-erf-gold text-erf-dark`} data-testid="est-save">{locked ? "Locked — save a revision" : "Save estimate"}</button>
              <Link to="/crm/estimating/rates" className="text-sm underline inline-flex min-h-[44px] items-center" data-testid="est-rates-link">Company rate library</Link>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
